/**
 * Targeted TanStack query-cache cleanup for auth transitions.
 *
 * `useWallet` previously called `queryClient.clear()` on logout AND
 * on the disconnect transient that happens mid-`signInActiveWallet`
 * (logout → loginOrLink). Clearing the entire cache is correct for
 * a real logout but catastrophic for a wallet switch: every shared
 * query (chat-messages, takeover-price, eth-price, lobby grid feed,
 * board metadata, etc.) gets nuked, then refires when the new SIWE
 * resolves. The resulting fanout — ~10 user-scoped queries plus all
 * the shared ones plus both SSE streams reconnecting — instantly
 * blows past the per-IP rate limit in `middleware.ts`, cascading
 * 429s across the UI.
 *
 * The fix is to scope the cleanup to entries whose query key
 * actually contains the viewer's address. Address-scoped queries
 * (chat-verify, db-notifications, portfolio, pending-fees, profile,
 * usdc-balance, etc.) are by convention keyed with the wallet
 * address as one of the segments; shared queries are not. A simple
 * EVM-address shape check (`/^0x[a-fA-F0-9]{40}$/`) on every
 * segment is enough to discriminate them.
 *
 * Exported as standalone helpers so they can be unit-tested without
 * standing up the full Privy/Wagmi provider tree, and so future
 * auth-transition consumers (a sign-out CTA, an account-switcher
 * outside chat) can call the same primitive instead of reaching
 * for `clear()` again.
 */

import type { QueryClient } from '@tanstack/react-query';

/**
 * Returns true when a query-key segment looks like an EVM address
 * (`0x` + 40 hex characters). Both lowercase and EIP-55 checksummed
 * forms match — TanStack stores keys verbatim, so callers like
 * `chat-panel.tsx` (checksummed straight from `wagmi.useAccount()`)
 * and `notifications` (lowercased before keying) both round-trip
 * through this predicate without normalisation.
 *
 * 42-character non-hex strings (e.g. an arbitrary base64 token of
 * the same length) intentionally don't match — the `0x` prefix
 * plus hex constraint is tight enough that no legitimate
 * non-address key segment in this codebase passes.
 */
export function isAddressKeySegment(seg: unknown): seg is string {
  return typeof seg === 'string' && /^0x[a-fA-F0-9]{40}$/.test(seg);
}

/**
 * Drop every cached entry whose key contains an address-shaped
 * segment. Resets observers attached to those keys back to
 * `{ data: undefined, isLoading: false }` (or `isLoading: true`
 * if still enabled) — the same behavior `removeQueries` always
 * has — and leaves shared, address-independent caches untouched.
 *
 * Use in place of `queryClient.clear()` whenever the goal is to
 * stop leaking the previous viewer's per-user data, NOT to nuke
 * everything they've ever loaded.
 */
export function removeAddressScopedQueries(qc: QueryClient): void {
  qc.removeQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey.some(isAddressKeySegment),
  });
}

/**
 * `resetQueries` every observer whose key contains any of the passed
 * addresses as a string segment. Address comparison is lower-cased so
 * both checksummed and lower-cased key segments match — TanStack stores
 * keys verbatim and our codebase has both forms in flight depending on
 * whether the key was minted from `wagmi.useAccount()` (checksummed) or
 * from an API response (lower-cased).
 *
 * `resetQueries` (not `removeQueries`) is what wallet-switch needs: it
 * flips active observers back to `{ data: undefined, isLoading: true }`
 * so the `placeholderData: (prev) => prev` default in `query-provider.tsx`
 * can't keep painting the previous wallet's balances / portfolio /
 * claimable while the new SIWE resolves. See `useWallet` wallet-switch
 * effect for the full rationale.
 *
 * Empty / falsy entries in `addresses` are skipped so callers can pass
 * `[previousAddress, currentAddress]` without pre-filtering.
 */
export function resetAddressKeyedQueries(
  qc: QueryClient,
  addresses: ReadonlyArray<string | null | undefined>,
): void {
  const lowered = addresses
    .filter((a): a is string => typeof a === 'string' && a.length > 0)
    .map((a) => a.toLowerCase());
  if (lowered.length === 0) return;
  qc.resetQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (!Array.isArray(key)) return false;
      for (const seg of key) {
        if (typeof seg !== 'string') continue;
        const segLower = seg.toLowerCase();
        for (const addr of lowered) {
          if (segLower === addr) return true;
        }
      }
      return false;
    },
  });
}

/**
 * `resetQueries` every observer whose key starts with any of the passed
 * prefixes. Thin wrapper around a `forEach` over the prefix list so
 * consumers aren't the ones driving that loop inline (and so the list
 * itself becomes greppable in one place).
 *
 * Used by `useWallet` alongside `resetAddressKeyedQueries` to belt-and-
 * brace the wallet-switch reset: some address-scoped queries (e.g.
 * `chat-verify`, `db-notifications`) are already covered by the address
 * predicate, but listing them explicitly by prefix protects against a
 * future refactor that drops the address from the key.
 */
export function resetQueriesByPrefixes(
  qc: QueryClient,
  prefixes: ReadonlyArray<string>,
): void {
  prefixes.forEach((prefix) => {
    qc.resetQueries({ queryKey: [prefix] });
  });
}
